import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiClient, { toasterFromError, formatDate, formatDuration } from '../../apiClient';
import { toast } from 'react-toastify';

const STATUS_MAP = {
  SUCCESS: { cls: 'success', label: 'Success', dot: '●' },
  FAILED:  { cls: 'failed',  label: 'Failed',  dot: '●' },
  VALIDATING: { cls: 'inprogress', label: 'Validating', dot: '●' },
  VALIDATED:  { cls: 'inprogress', label: 'Validated',  dot: '●' },
  UPLOADING:  { cls: 'inprogress', label: 'Uploading',  dot: '●' },
  INSTALLING: { cls: 'inprogress', label: 'Installing', dot: '●' },
  REQUESTED:  { cls: 'inprogress', label: 'Requested',  dot: '●' },
  IN_PROGRESS:{ cls: 'inprogress', label: 'In Progress',dot: '●' }
};

function statusFor(s) {
  if (!s) return { cls: 'none', label: 'No OTA history', dot: '○' };
  return STATUS_MAP[s] || { cls: 'none', label: s, dot: '○' };
}

function LoadingSpinner({ text }) {
  return (
    <div className="loading-spinner-wrap">
      <div className="spinner-border text-info" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <div style={{ marginTop: 12, fontSize: 13 }}>{text || 'Loading…'}</div>
    </div>
  );
}

function HistoryTable({ rows }) {
  const [expanded, setExpanded] = useState(new Set());
  const toggle = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };
  if (!rows || rows.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-title">No OTA history available for this application.</div>
        <div style={{ fontSize: 12 }}>History is captured as firmware is uploaded through the Update Firmware tab.</div>
      </div>
    );
  }
  return (
    <div className="table-responsive">
      <table className="data-table">
        <thead>
          <tr>
            <th>From</th>
            <th>To</th>
            <th>Status</th>
            <th>Date</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const key = r.id || idx;
            const st = statusFor(r.status);
            const isOpen = expanded.has(key);
            return (
              <React.Fragment key={key}>
                <tr className="clickable" onClick={() => toggle(key)}>
                  <td style={{ fontFamily: 'ui-monospace, monospace' }}>{r.from_version || '—'}</td>
                  <td style={{ fontFamily: 'ui-monospace, monospace' }}>{r.to_version}</td>
                  <td>
                    <span className={`status-badge ${st.cls}`}>
                      <span className={`status-dot ${st.cls}`}></span>
                      {st.label}
                    </span>
                  </td>
                  <td>{formatDate(r.completed_ts || r.request_ts, { short: true })}</td>
                  <td>{formatDuration(r.duration_ms) || '—'}</td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={5} style={{ padding: 0 }}>
                      <div className="detail-card">
                        <div><span className="k">OTA ID:</span>{r.ota_id || '—'}</div>
                        <div><span className="k">Firmware file:</span>{r.firmware_filename || '—'}</div>
                        <div><span className="k">Request time:</span>{formatDate(r.request_ts)}</div>
                        <div><span className="k">Start time:</span>{formatDate(r.start_ts)}</div>
                        <div><span className="k">Completion time:</span>{formatDate(r.completed_ts)}</div>
                        <div><span className="k">Duration:</span>{formatDuration(r.duration_ms) || '—'}</div>
                        {r.error_message && (
                          <div><span className="k">Failure reason:</span><span style={{ color: '#FF5C8A' }}>{r.error_message}</span></div>
                        )}
                        {r.backend_response && (
                          <div>
                            <span className="k">Backend response:</span>
                            <code style={{ color: '#ECF0F5', background: '#0F1416', padding: '2px 6px', borderRadius: 3, fontSize: 12 }}>
                              {String(r.backend_response).length > 200 ? String(r.backend_response).slice(0, 200) + '…' : r.backend_response}
                            </code>
                          </div>
                        )}
                        {r.user_source && <div><span className="k">Source:</span>{r.user_source}</div>}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AppLogsPane({ appName }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [level, setLevel] = useState('ALL');
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const resp = await apiClient.get(`/api/applications/${encodeURIComponent(appName)}/logs`, {
        params: { limit: LIMIT, offset, level }
      });
      setLogs(resp.data.logs || []);
      setTotal(resp.data.total || 0);
    } catch (e) {
      setErr(toasterFromError(e, 'Unable to load application logs'));
    } finally {
      setLoading(false);
    }
  }, [appName, offset, level]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingSpinner text="Loading application logs…" />;
  if (err) return (
    <div className="error-alert">
      <span>{err}</span>
      <button className="btn-toggle on" onClick={load}>Retry</button>
    </div>
  );
  if (logs.length === 0) return (
    <div className="empty-state">
      <div className="empty-state-title">No application logs yet.</div>
    </div>
  );
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
        <select className="filter-select" value={level} onChange={(e) => { setLevel(e.target.value); setOffset(0); }}>
          {['ALL','INFO','SUCCESS','WARNING','ERROR','DEBUG'].map(l => <option key={l} value={l}>Level: {l}</option>)}
        </select>
        <div style={{ fontSize: 12, color: '#8a9396' }}>
          Showing {offset + 1}–{Math.min(total, offset + LIMIT)} of {total}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-toggle" disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - LIMIT))}>Prev</button>
          <button className="btn-toggle" disabled={offset + LIMIT >= total} onClick={() => setOffset(o => o + LIMIT)}>Next</button>
        </div>
      </div>
      <div className="logs-viewer">
        {logs.slice().reverse().map((l, i) => (
          <div className="log-entry" key={l.id || i}>
            <span className="log-entry-time">{l.timestamp ? formatDate(l.timestamp).split(' ').slice(-1)[0] : ''}</span>
            <span className={`level-badge ${l.level || 'INFO'}`}>{l.level || 'INFO'}</span>
            <span className="log-entry-body">
              {l.method && <span className={`tag method ${l.method}`} style={{ marginRight: 6 }}>{l.method}</span>}
              {l.status_code != null && <span className="tag" style={{ marginRight: 6 }}>{l.status_code}</span>}
              {l.ota_id && <span className="tag ota" style={{ marginRight: 6 }}>{l.ota_id}</span>}
              {l.message}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExpandedPane({ app, onCollapse }) {
  const [sub, setSub] = useState('history');
  const [history, setHistory] = useState([]);
  const [loadingH, setLoadingH] = useState(true);
  const [errH, setErrH] = useState(null);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingH(true); setErrH(null);
      try {
        const r = await apiClient.get(`/api/applications/${encodeURIComponent(app.name)}/history`, { params: { limit: 100 } });
        if (mounted) setHistory(r.data.history || []);
      } catch (e) {
        if (mounted) setErrH(toasterFromError(e, 'Unable to load OTA history'));
      } finally {
        if (mounted) setLoadingH(false);
      }
    })();
    return () => { mounted = false; };
  }, [app.name, retryTick]);

  return (
    <div className="expand-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#ECF0F5' }}>
          {app.name} <span style={{ color: '#27EF9F', fontSize: 13 }}>v{app.current_version}</span>
        </div>
        <button className="btn-toggle" onClick={onCollapse}>Collapse</button>
      </div>
      <div className="sub-tabs">
        <div className={`sub-tab ${sub === 'history' ? 'active' : ''}`} onClick={() => setSub('history')}>OTA History</div>
        <div className={`sub-tab ${sub === 'logs' ? 'active' : ''}`} onClick={() => setSub('logs')}>Application Logs</div>
      </div>
      {sub === 'history' && (
        loadingH ? <LoadingSpinner text="Loading OTA history…" /> :
        errH ? <div className="error-alert"><span>{errH}</span><button className="btn-toggle on" onClick={() => setRetryTick(t => t + 1)}>Retry</button></div> :
        <HistoryTable rows={history} />
      )}
      {sub === 'logs' && <AppLogsPane appName={app.name} />}
    </div>
  );
}

function Card({ app, expanded, onToggleExpand }) {
  const st = statusFor(app.last_ota_status);
  return (
    <div className="col">
      <div className="app-card">
        <div className="app-card-header">
          <div>
            <div className="app-card-title">{app.name}</div>
          </div>
        </div>
        <div className="app-card-current-version">
          <i className="fa fa-code-branch" style={{ marginRight: 6 }}></i>
          Current Version: <span style={{ fontWeight: 700 }}>{app.current_version || '—'}</span>
        </div>
        <div className="app-card-section-label">Available Versions</div>
        <ul className="app-card-versions">
          {(app.versions || []).slice(0, 10).map((v, i) => (
            <li key={v + '_' + i} className={i === 0 ? 'current' : ''}>
              {i === 0 ? '●' : '○'} {v} {i === 0 && <span style={{ marginLeft: 6 }} className="status-badge success">Current</span>}
            </li>
          ))}
          {(app.versions || []).length > 10 && (
            <li style={{ color: '#8a9396', fontStyle: 'italic' }}>+{(app.versions || []).length - 10} more…</li>
          )}
          {(app.versions || []).length === 0 && <li style={{ color: '#6C6C6C', fontStyle: 'italic' }}>No version records</li>}
        </ul>
        <div className="app-card-section-label">
          Versions: {app.version_count || (app.versions ? app.versions.length : 0)}
        </div>
        <div className="app-card-ota-row">
          <div>
            <span className={`status-dot ${st.cls}`}></span>
            <span>Last OTA: </span>
            <span className={`status-badge ${st.cls}`} style={{ marginLeft: 4 }}>{st.label}</span>
          </div>
          {app.last_ota_timestamp && (
            <div>Last Updated: {formatDate(app.last_ota_timestamp, { short: true, withTime: true })}</div>
          )}
          {app.last_ota_from != null || app.last_ota_to != null ? (
            <div style={{ fontFamily: 'ui-monospace, monospace' }}>
              <span style={{ color: '#b0b0b0' }}>{app.last_ota_from || '(new app)'}</span>
              <span style={{ margin: '0 6px', color: '#0DB8DE' }}>→</span>
              <span style={{ color: '#27EF9F' }}>{app.last_ota_to || '—'}</span>
            </div>
          ) : null}
          {app.last_ota_error && !expanded && (
            <div style={{ color: '#FF5C8A', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              ⚠ {app.last_ota_error}
            </div>
          )}
        </div>
        <div style={{ height: 16 }} />
        <button className="btn-primary-sm" type="button" onClick={() => onToggleExpand(app.name)}>
          <i className={expanded ? 'fa fa-chevron-up' : 'fa fa-chevron-down'} style={{ marginRight: 6 }} aria-hidden="true"></i>
          {expanded ? 'Show Less' : 'Show More'}
        </button>
        {expanded && <ExpandedPane app={app} onCollapse={() => onToggleExpand(app.name)} />}
      </div>
    </div>
  );
}

const ApplicationsTab = ({ onGoUpload }) => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await apiClient.get('/api/applications');
      setApps(r.data.applications || []);
    } catch (e) {
      setErr(toasterFromError(e, 'Unable to load applications'));
      toast.error(toasterFromError(e, 'Unable to load applications'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = useCallback((name) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return apps;
    return apps.filter(a => String(a.name).toLowerCase().includes(s));
  }, [apps, search]);

  if (loading) return <LoadingSpinner text="Loading applications…" />;
  if (err) return (
    <div>
      <div className="error-alert">
        <span>Unable to load applications. {err}</span>
        <button className="btn-toggle on" onClick={load}><i className="fa fa-sync" style={{ marginRight: 6 }}></i>Retry</button>
      </div>
    </div>
  );
  if (apps.length === 0) return (
    <div className="empty-state">
      <i className="fa fa-cubes" style={{ fontSize: 32, marginBottom: 14, color: '#0DB8DE' }} aria-hidden="true"></i>
      <div className="empty-state-title">No applications found.</div>
      <div style={{ marginBottom: 14, fontSize: 13 }}>Upload your first firmware to create an application.</div>
      <button className="btn-primary-lg" onClick={onGoUpload} type="button">
        <i className="fa fa-cloud-upload-alt" style={{ marginRight: 8 }} aria-hidden="true"></i>
        Upload Firmware
      </button>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ color: '#ECF0F5', fontSize: 20, fontWeight: 700, margin: 0 }}>
            Applications <span style={{ color: '#8a9396', fontWeight: 400, fontSize: 14 }}>({apps.length} total)</span>
          </h2>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <i className="fa fa-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#8a9396', fontSize: 12 }} aria-hidden="true"></i>
            <input
              type="text"
              className="form-control"
              placeholder="Search applications…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 30, background: '#0F1416', border: '1px solid #222c30', color: '#ECF0F5', borderRadius: 4, width: 260 }}
            />
          </div>
          <button className="btn-toggle" onClick={load} title="Refresh applications">
            <i className="fa fa-sync" style={{ marginRight: 6 }} aria-hidden="true"></i>Refresh
          </button>
          <button className="btn-primary-lg" onClick={onGoUpload} type="button">
            <i className="fa fa-plus" style={{ marginRight: 6 }} aria-hidden="true"></i>Upload Firmware
          </button>
        </div>
      </div>
      {filtered.length === 0 && search ? (
        <div className="empty-state">
          <div className="empty-state-title">No applications match “{search}”.</div>
        </div>
      ) : (
        <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-4">
          {filtered.map(a => (
            <Card key={a.name} app={a} expanded={expanded.has(a.name)} onToggleExpand={toggle} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ApplicationsTab;
